import React, { useState, useEffect, useContext } from 'react';
import classNames from 'classnames/bind';
import { FaEdit, FaGripVertical } from 'react-icons/fa';
import styles from './AdminProjectTypes.module.css';
import { getData } from '~/helper/apiService';
import { LoginContext } from '~/component/Context/LoginProvider';
import { ModalOverLayContext } from '~/component/Context/ModalOverLayProvider';

const cx = classNames.bind(styles);

// Modal Edit đơn giản (giữ nguyên)
const EditProjectTypeModal = ({ id, currentName, onSave, onClose }) => {
  const [name, setName] = useState(currentName);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (name.trim()) {
      onSave(id, name.trim());
      onClose();
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <h3>Sửa Project Type</h3>
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Tên project type"
            required
          />
          <div className={styles.modalButtons}>
            <button type="submit">Lưu</button>
            <button type="button" onClick={onClose}>Hủy</button>
          </div>
        </form>
      </div>
    </div>
  );
};

function AdminProjectTypes() {
  const { loginStatus } = useContext(LoginContext);
  const { setModalComponentContent } = useContext(ModalOverLayContext);
  const [data, setData] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Callbacks cho loading từ apiService
  const startLoading = () => setIsLoading(true);
  const stopLoading = () => setIsLoading(false);

  // Load data sử dụng getData từ apiService
  const loadData = async () => {
    try {
      const responseData = await getData('/projects/readType', {}, startLoading, stopLoading);
      if (responseData) {
        // Sắp xếp theo position để đảm bảo thứ tự ban đầu (đồng bộ với DB)
        const sortedData = responseData.sort((a, b) => a.position - b.position);
        setData(sortedData);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      stopLoading();
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Hàm PUT update (giữ nguyên, hỗ trợ cả single và bulk)
  const updateItem = async (endpoint, payload) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}${endpoint}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error updating:', error);
      throw error;
    }
  };

  // Xử lý kéo thả để sắp xếp - Cho phép kéo từ toàn bộ item, không chỉ handle
  const handleDragStart = (e, index) => {
    // Loại bỏ kiểm tra handle để dễ kéo hơn, chỉ cần click và kéo từ bất cứ đâu (trừ button edit)
    const target = e.target.closest('button');
    if (target) {
      e.preventDefault();
      return; // Không drag nếu click vào button
    }
    e.dataTransfer.setData('text/plain', index.toString());
    e.dataTransfer.effectAllowed = 'move';
    setDraggedIndex(index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
    if (dragIndex === dropIndex) return;

    const newData = [...data];
    const [draggedItem] = newData.splice(dragIndex, 1);
    newData.splice(dropIndex, 0, draggedItem);
    // Cập nhật position tạm thời trong state để hiển thị đồng bộ ngay lập tức
    newData.forEach((item, idx) => {
      item.tempPosition = idx + 1; // Sử dụng tempPosition để tránh conflict với DB position
    });
    setData(newData);
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Nút lưu thứ tự - gửi một object chứa mảng các {id, position} và đồng bộ lại sau khi lưu
  const handleSaveOrder = async () => {
    try {
      setIsLoading(true);
      // Tạo mảng updates từ data hiện tại (sau khi drag-drop)
      const updates = data.map((item, index) => ({
        id: item.id,
        position: index + 1
      }));
      // Gửi một request PUT với payload { updates: [...] }
      await updateItem('/projects/updateTypesPositions', { updates });
      alert('Lưu thứ tự thành công');
      // Reload để đồng bộ với DB (xóa tempPosition)
      loadData();
    } catch (err) {
      alert('Lỗi lưu thứ tự');
      console.error('Error saving order:', err);
      // Nếu lỗi, reload để rollback về trạng thái DB
      loadData();
    } finally {
      setIsLoading(false);
    }
  };

  // Edit (giữ nguyên, vẫn dùng single update cho name)
  const handleEdit = (id, currentName) => {
    setModalComponentContent(
      <EditProjectTypeModal
        id={id}
        currentName={currentName}
        onSave={async (id, name) => {
          try {
            await updateItem(`/projects/updateType/${id}`, { name });
            loadData(); // Reload để đồng bộ
          } catch (err) {
            alert('Lỗi cập nhật tên');
          }
        }}
        onClose={() => setModalComponentContent(null)}
      />
    );
  };

  if (isLoading) return <div className={cx('loading')}>Đang tải...</div>;
  if (!loginStatus) return <div>Bạn cần đăng nhập để quản lý.</div>;

  return (
    <div className={cx('admin-container')}>
      <h2>Quản lý Project Types</h2>
      <div className={cx('button-group')}>
        <button onClick={loadData} className={cx('refresh-btn')} disabled={isLoading}>
          Làm mới
        </button>
        <button onClick={handleSaveOrder} className={cx('save-btn')} disabled={isLoading}>
          Lưu thứ tự
        </button>
      </div>
      <div className={cx('list')}>
        {data.length === 0 ? (
          <p>Không có dữ liệu</p>
        ) : (
          data.map((item, index) => (
            <div
              key={item.id}
              className={cx('type-item', { dragging: draggedIndex === index })}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
            >
              <div className={cx('drag-handle')}>
                <FaGripVertical />
              </div>
              <span className={cx('position')}>
                {item.tempPosition || item.position || (index + 1)}
              </span>
              <span className={cx('name')}>{item.name}</span>
              <div className={cx('actions')}>
                <button onClick={() => handleEdit(item.id, item.name)} title="Sửa">
                  <FaEdit />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default AdminProjectTypes;